import { useRef, useState } from 'react'
import { FileText, UploadCloud } from 'lucide-react'
import { FormLabel } from '@/components/custom/form-label'
import { useIsMobile } from '@/hooks'

interface DragDropInputProps {
  onFileSelect: (file: File | null) => void
  selectedFile?: File | null
  accept?: string
  label?: string
  required?: boolean
  error?: string
  previewUrl?: string | null
  existingFileName?: string | null
  className?: string
  disabled?: boolean
}

const truncateFilename = (name: string, prefixLen = 8) => {
  const dotIndex = name.lastIndexOf('.')
  const ext = dotIndex !== -1 ? name.slice(dotIndex) : ''
  const base = dotIndex !== -1 ? name.slice(0, dotIndex) : name
  if (base.length <= prefixLen) return name
  return base.slice(0, prefixLen) + '....' + ext
}

export function DragDropInput({
  onFileSelect,
  selectedFile,
  accept = 'image/*,application/pdf',
  label,
  required = false,
  error,
  previewUrl,
  existingFileName,
  className = '',
  disabled = false,
}: DragDropInputProps) {
  const isMobile = useIsMobile()
  const [isDragActive, setIsDragActive] = useState<boolean>(false)

  // Two refs: one for file picker, one for camera capture
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const dragCounter = useRef<number>(0)

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragCounter.current += 1
    if (e.dataTransfer.items.length) {
      setIsDragActive(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragCounter.current -= 1
    if (dragCounter.current <= 0) {
      setIsDragActive(false)
      dragCounter.current = 0
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    onFileSelect(file)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    onFileSelect(file)
    setIsDragActive(false)
    dragCounter.current = 0
  }

  // Open file picker for image/PDF
  const handleSelectFileClick = () => fileInputRef.current?.click()

  // Open camera directly (mobile only)
  // const handleCameraClick = () => cameraInputRef.current?.click()

  // Reset file inputs when selectedFile changes to null
  const resetInputs = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (cameraInputRef.current) cameraInputRef.current.value = ''
  }

  // Reset inputs when file is cleared
  if (
    !selectedFile &&
    (fileInputRef.current?.value || cameraInputRef.current?.value)
  ) {
    resetInputs()
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <FormLabel required={required}>{label}</FormLabel>}

      <div
        className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center transition-transform duration-150 ${
          disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
            : isDragActive
              ? 'border-blue-400 bg-blue-50 scale-105 shadow-md cursor-pointer'
              : error
                ? 'border-red-400 bg-red-50 cursor-pointer'
                : 'border-gray-300 hover:bg-gray-50 cursor-pointer'
        }`}
        onDrop={disabled ? undefined : handleDrop}
        onDragOver={disabled ? undefined : handleDragOver}
        onDragEnter={disabled ? undefined : handleDragEnter}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onClick={disabled ? undefined : handleSelectFileClick}
        role="button"
        aria-label={label || 'Upload file'}
        aria-disabled={disabled}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Preview"
            className="max-h-40 mb-2 rounded"
          />
        ) : existingFileName && !selectedFile ? (
          <div className="flex flex-col items-center">
            <FileText className="h-8 w-8 mb-2 text-gray-500" />
            <span className="text-sm text-gray-700 font-medium px-2 text-center">
              {truncateFilename(existingFileName)}
            </span>
            <span className="text-xs text-gray-400 mt-1">
              Click to replace
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <UploadCloud
              className={`h-8 w-8 mb-2 ${
                isDragActive
                  ? 'text-blue-500'
                  : error
                    ? 'text-red-400'
                    : 'text-gray-400'
              }`}
            />
            <span
              className={`text-sm ${
                isDragActive
                  ? 'text-blue-500'
                  : error
                    ? 'text-red-500'
                    : 'text-gray-500'
              }`}
            >
              {isDragActive
                ? 'Release to upload'
                : isMobile
                  ? 'Select a file'
                  : 'Drag & drop or click to select a file'}
            </span>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled}
      />
    </div>
  )
}
